import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { CallEventType } from '@brimo/shared';
import { UNAUTHENTICATED_EVENT, errorMessage } from '../../lib/api';
import { queryRoots } from '../../lib/events';
import { useWorkspace } from '../../lib/session';
import { createCall, reportCallEvent, reportPresence, type CallView, type StartCallInput } from './api';
import { IDLE_CALL, isLive, type CallSnapshot } from './callState';
import {
  MicrophoneError,
  destroyVoiceClient,
  getVoiceClient,
  isMicrophoneFailure,
  type BrimoIncomingCall,
  type BrimoVoiceCall,
} from './voice';

/**
 * One softphone call per tab, shared by the dialer and the contact record (E1): a small external store
 * with the call's phase, the voice call behind the seam and the queue of event reports to the server.
 * Dialer users also listen for inbound calls (`listen`): the ringing call sits in `incoming` until it is accepted —
 * then it is this tab's call like an outbound one from the record (`source: 'record'`, `direction: 'inbound'`) — or
 * declined. While listening the tab reports its registration (`POST /telephony/presence`) so the server rings it.
 */

const ALREADY_CALLING = 'Sie telefonieren bereits.';
const CONNECTION_FAILED = 'Die Verbindung ist fehlgeschlagen. Bitte versuchen Sie es erneut.';

let snapshot: CallSnapshot = IDLE_CALL;
let activeCall: BrimoVoiceCall | null = null;
let unsubscribers: Array<() => void> = [];
let reports: Promise<unknown> = Promise.resolve();
let hangupRequested = false;
let deviceUserId: string | null = null;
let queryClient: QueryClient | null = null;
let pendingIncoming: BrimoIncomingCall | null = null;
let listening = false;
let presenceTimer: number | null = null;
let cacheUnsubscribe: (() => void) | null = null;
const listeners = new Set<() => void>();

function beforeUnload(event: BeforeUnloadEvent): void {
  event.preventDefault();
}

function set(patch: Partial<CallSnapshot>): void {
  const wasLive = isLive(snapshot.phase);
  snapshot = { ...snapshot, ...patch };
  const live = isLive(snapshot.phase);
  if (live && !wasLive) window.addEventListener('beforeunload', beforeUnload);
  if (!live && wasLive) window.removeEventListener('beforeunload', beforeUnload);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): CallSnapshot {
  return snapshot;
}

function refreshQueries(): void {
  if (queryClient === null) return;
  for (const root of [queryRoots.dialer, queryRoots.contact, queryRoots.calls]) {
    void queryClient.invalidateQueries({ queryKey: [...root] });
  }
}

/** Reports one transition; reports of a call run one after another so the server sees them in order. */
function report(callId: string, type: CallEventType, at: string, reason?: string): void {
  reports = reports
    .catch(() => undefined)
    .then(() => reportCallEvent(callId, { type, at, reason }))
    .then(
      (view: CallView) => {
        if (snapshot.callId === callId) set({ view });
        if (type === 'ended' || type === 'failed' || type === 'connected') refreshQueries();
      },
      (error: unknown) => {
        if (snapshot.callId === callId && (type === 'ended' || type === 'failed')) {
          set({ error: errorMessage(error) });
        }
      },
    );
}

function detach(): void {
  for (const unsubscribe of unsubscribers) unsubscribe();
  unsubscribers = [];
  activeCall = null;
  hangupRequested = false;
}

function finish(phase: 'ended' | 'failed', callId: string, at: string, reason?: string): void {
  if (snapshot.callId !== callId || !isLive(snapshot.phase)) return;
  detach();
  set({
    phase,
    ringing: false,
    endedAt: Date.now(),
    muted: false,
    localStream: null,
    remoteStream: null,
    error: phase === 'failed' ? CONNECTION_FAILED : snapshot.error,
  });
  report(callId, phase, at, reason);
}

function attach(call: BrimoVoiceCall): void {
  activeCall = call;
  const { callId } = call;
  unsubscribers = [
    call.on('ringing', (detail) => {
      if (snapshot.callId !== callId || snapshot.phase !== 'dialing') return;
      set({ ringing: true });
      report(callId, 'ringing', detail.at);
    }),
    call.on('connected', (detail) => {
      if (snapshot.callId !== callId || snapshot.phase !== 'dialing') return;
      set({
        phase: 'connected',
        ringing: false,
        connectedAt: Date.now(),
        localStream: call.localStream(),
        remoteStream: call.remoteStream(),
      });
      report(callId, 'connected', detail.at);
    }),
    call.on('ended', (detail) => finish('ended', callId, detail.at, detail.reason)),
    call.on('failed', (detail) => finish('failed', callId, detail.at, detail.reason)),
  ];
  set({ localStream: call.localStream(), remoteStream: call.remoteStream() });
  if (hangupRequested) call.hangup();
}

async function microphoneDenied(): Promise<boolean> {
  try {
    const status = await navigator.permissions.query({ name: 'microphone' });
    return status.state === 'denied';
  } catch {
    // Browsers without the permission name: the device's own request decides.
    return false;
  }
}

/** Starts a call: token/device → `POST /calls` → connect with the call id. Returns false on a refusal. */
async function start(input: StartCallInput): Promise<boolean> {
  if (isLive(snapshot.phase)) {
    set({ error: ALREADY_CALLING });
    return false;
  }
  detach();
  const deviceReady = snapshot.deviceReady;
  set({
    ...IDLE_CALL,
    deviceReady,
    incoming: snapshot.incoming,
    phase: 'dialing',
    contactId: input.contactId,
    source: input.source,
    sessionId: input.sessionId ?? null,
    direction: 'outbound',
  });
  const client = getVoiceClient();
  try {
    await client.ready();
    set({ deviceReady: true });
    if (client.kind === 'twilio' && (await microphoneDenied())) throw new MicrophoneError();
  } catch (error) {
    set({
      phase: 'idle',
      error: isMicrophoneFailure(error) ? new MicrophoneError().message : errorMessage(error),
    });
    return false;
  }

  let view: CallView;
  try {
    view = await createCall(input);
  } catch (error) {
    set({ phase: 'idle', error: errorMessage(error) });
    return false;
  }
  set({ callId: view.id, view });
  refreshQueries();

  try {
    const call = await client.connect({ callId: view.id });
    if (snapshot.callId !== view.id) {
      call.hangup();
      return false;
    }
    attach(call);
    return true;
  } catch (error) {
    const microphone = isMicrophoneFailure(error);
    const at = new Date().toISOString();
    set({
      phase: 'failed',
      endedAt: Date.now(),
      error: microphone ? new MicrophoneError().message : CONNECTION_FAILED,
    });
    report(view.id, 'failed', at, microphone ? 'microphone' : 'connect');
    return false;
  }
}

function hangup(): void {
  if (!isLive(snapshot.phase)) return;
  if (activeCall === null) {
    hangupRequested = true;
    return;
  }
  activeCall.hangup();
}

function mute(on: boolean): void {
  if (activeCall === null || snapshot.phase !== 'connected') return;
  activeCall.mute(on);
  set({ muted: activeCall.isMuted() });
}

/** Back to idle after the result was saved (or to drop a refusal banner). Ignored while a call is live. */
function reset(): void {
  if (isLive(snapshot.phase)) return;
  detach();
  set({ ...IDLE_CALL, deviceReady: snapshot.deviceReady, incoming: snapshot.incoming });
}

// --- inbound calls ------------------------------------------------------------------------------------

function sendPresence(registered: boolean): void {
  reportPresence(registered).catch(() => undefined);
}

function stopPresence(): void {
  if (presenceTimer !== null) window.clearInterval(presenceTimer);
  presenceTimer = null;
}

function startPresence(): void {
  stopPresence();
  reportPresence(true).then(
    (answer) => {
      stopPresence();
      presenceTimer = window.setInterval(() => sendPresence(true), answer.heartbeatSeconds * 1000);
    },
    () => undefined,
  );
}

function clearIncoming(call: BrimoIncomingCall): void {
  if (pendingIncoming !== call) return;
  pendingIncoming = null;
  set({ incoming: null });
}

function onIncoming(call: BrimoIncomingCall): void {
  // One call per tab: a second ringing call or one during a live call is declined (Twilio rings the others).
  if (pendingIncoming !== null || isLive(snapshot.phase) || call.callId === '') {
    call.reject();
    return;
  }
  pendingIncoming = call;
  call.onCanceled(() => clearIncoming(call));
  set({
    incoming: {
      callId: call.callId,
      contactId: call.contactId,
      contactName: call.contactName,
      from: call.from,
    },
  });
}

/** Registers the tab for inbound calls once (dialer users; the shell calls it). */
function listen(): void {
  if (listening) return;
  const client = getVoiceClient();
  if (client.listen === undefined) return;
  listening = true;
  client.listen(onIncoming).then(
    (allowed) => {
      if (!allowed) {
        listening = false;
        return;
      }
      set({ deviceReady: true });
      startPresence();
    },
    () => {
      listening = false;
    },
  );
}

/** „Annehmen": the ringing call becomes the tab's call; returns its contact, null when nothing rings. */
function acceptIncoming(): string | null {
  const call = pendingIncoming;
  if (call === null || isLive(snapshot.phase)) return null;
  pendingIncoming = null;
  detach();
  set({
    ...IDLE_CALL,
    deviceReady: snapshot.deviceReady,
    phase: 'dialing',
    callId: call.callId,
    contactId: call.contactId,
    source: 'record',
    direction: 'inbound',
  });
  attach(call.accept());
  refreshQueries();
  return call.contactId;
}

/** „Ablehnen": Twilio rings the other softphones or plays the call-back promise to the caller. */
function rejectIncoming(): void {
  const call = pendingIncoming;
  if (call === null) return;
  call.reject();
  clearIncoming(call);
}

function dismissError(): void {
  if (snapshot.error !== null) set({ error: null });
}

/** Sign-out or another user: hang up, destroy the device, forget the call. */
function teardown(): void {
  activeCall?.hangup();
  detach();
  pendingIncoming?.reject();
  pendingIncoming = null;
  if (listening) sendPresence(false);
  listening = false;
  stopPresence();
  destroyVoiceClient();
  deviceUserId = null;
  set({ ...IDLE_CALL });
}

function prepare(): void {
  const client = getVoiceClient();
  client.ready().then(
    () => set({ deviceReady: true }),
    () => set({ deviceReady: false }),
  );
}

function onUnauthenticated(): void {
  teardown();
}

/** Registers the tab's query client once (refreshes after reports; sign-out = the session query removed). */
function bindQueryClient(client: QueryClient): void {
  if (queryClient === client) return;
  cacheUnsubscribe?.();
  queryClient = client;
  cacheUnsubscribe = client.getQueryCache().subscribe((event) => {
    const key = (event.query as { queryKey: readonly unknown[] }).queryKey;
    const root = key[0];
    if (event.type === 'removed' && root === 'session') teardown();
  });
  window.removeEventListener(UNAUTHENTICATED_EVENT, onUnauthenticated);
  window.addEventListener(UNAUTHENTICATED_EVENT, onUnauthenticated);
}

export interface CallControls {
  state: CallSnapshot;
  start: (input: StartCallInput) => Promise<boolean>;
  hangup: () => void;
  mute: (on: boolean) => void;
  reset: () => void;
  dismissError: () => void;
  /** Loads token and device ahead of the first call („Softphone verbunden"). */
  prepare: () => void;
  /** Registers the tab for inbound calls (dialer users). */
  listen: () => void;
  acceptIncoming: () => string | null;
  rejectIncoming: () => void;
}

/** The tab's softphone. */
export function useCall(): CallControls {
  const client = useQueryClient();
  const workspace = useWorkspace();
  const userId = workspace.user.id;
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    bindQueryClient(client);
  }, [client]);

  useEffect(() => {
    if (deviceUserId !== null && deviceUserId !== userId) teardown();
    deviceUserId = userId;
  }, [userId]);

  return useMemo(
    () => ({
      state,
      start,
      hangup,
      mute,
      reset,
      dismissError,
      prepare,
      listen,
      acceptIncoming,
      rejectIncoming,
    }),
    [state],
  );
}

/** One ticker for every timer on the page, so the queue bar, the call bar and the status line agree. */
const clockListeners = new Set<() => void>();
let clockNow = Date.now();
let clockTimer: number | null = null;

function subscribeClock(listener: () => void): () => void {
  clockListeners.add(listener);
  if (clockTimer === null) {
    clockNow = Date.now();
    clockTimer = window.setInterval(() => {
      clockNow = Date.now();
      for (const notify of clockListeners) notify();
    }, 1000);
  }
  return () => {
    clockListeners.delete(listener);
    if (clockListeners.size === 0 && clockTimer !== null) {
      window.clearInterval(clockTimer);
      clockTimer = null;
    }
  };
}

function subscribeNever(): () => void {
  return () => undefined;
}

/** Now (ms), ticking every second while `running`; used for the call timer. */
export function useClock(running: boolean): number {
  return useSyncExternalStore(running ? subscribeClock : subscribeNever, () => (running ? clockNow : 0));
}
